import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks';
import { authApi } from '@/api/http-client';
import type { Session } from '@/types';
import { Button, Card, Badge } from '@medikei/ui';
import { Skeleton } from '@medikei/ui';
import { ReferralCard } from '@/components/referral';
import {
  User as UserIcon,
  Mail,
  Shield,
  Calendar,
  Clock,
  Monitor,
  Smartphone,
  LogOut,
  LogOutIcon,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export function ProfilePage() {
  const { user, logout, logoutAll, isRefreshing } = useAuth();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(true);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  useEffect(() => {
    const loadSessions = async () => {
      try {
        const response = await authApi.getSessions();
        setSessions(response.sessions || []);
        setActiveSessionId(response.activeSessionId || null);
      } catch {
        setSessions([]);
      } finally {
        setIsLoadingSessions(false);
      }
    };
    loadSessions();
  }, []);

  const handleLogoutSession = async (sessionId: string) => {
    try {
      await authApi.logoutSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } catch {
      // Handle error
    }
  };

  const getDeviceIcon = (userAgent?: string) => {
    if (!userAgent) return Monitor;
    if (userAgent.toLowerCase().includes('mobile')) return Smartphone;
    return Monitor;
  };

  const formatDate = (date: string) => {
    try {
      return format(new Date(date), "dd 'de' MMMM 'de' yyyy 'às' HH:mm", {
        locale: ptBR,
      });
    } catch {
      return date;
    }
  };

  const formatRelative = (date: string) => {
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true, locale: ptBR });
    } catch {
      return date;
    }
  };

  if (!user) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Meu Perfil</h1>
          <p className="text-muted-foreground mt-1">Carregando informações...</p>
        </div>
        <Card className="p-6">
          <div className="space-y-4">
            <Skeleton variant="rectangular" height={60} />
            <Skeleton variant="rectangular" height={60} />
            <Skeleton variant="rectangular" height={60} />
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold tracking-tight text-foreground">Meu Perfil</h1>
        <p className="text-muted-foreground mt-2 text-base">
          Gerencie suas informações pessoais e segurança da conta
        </p>
      </div>

      <ReferralCard />

      <div className="grid gap-8 lg:grid-cols-5">
        <Card className="p-6 lg:col-span-3 border-0 shadow-lg shadow-black/5 bg-gradient-to-br from-white to-slate-50/50">
          <div className="flex items-center gap-4 mb-6 pb-5 border-b border-border">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <UserIcon className="text-white" size={28} />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Informações da Conta</h2>
              <p className="text-sm text-muted-foreground">Seus dados pessoais</p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50/80 border border-slate-200/60 group hover:border-blue-200 hover:bg-blue-50/30 transition-all duration-200">
              <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200/70 transition-colors">
                <UserIcon className="text-blue-600" size={20} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Nome</p>
                <p className="font-semibold text-foreground truncate">{user.name || 'Não informado'}</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50/80 border border-slate-200/60 group hover:border-blue-200 hover:bg-blue-50/30 transition-all duration-200">
              <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200/70 transition-colors">
                <Mail className="text-blue-600" size={20} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Email</p>
                <p className="font-semibold text-foreground truncate">{user.email}</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50/80 border border-slate-200/60 group hover:border-blue-200 hover:bg-blue-50/30 transition-all duration-200">
              <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200/70 transition-colors">
                <Shield className="text-blue-600" size={20} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Função</p>
                <Badge variant="primary" className="mt-1">{user.role}</Badge>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50/80 border border-slate-200/60 group hover:border-blue-200 hover:bg-blue-50/30 transition-all duration-200">
              <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200/70 transition-colors">
                <Calendar className="text-blue-600" size={20} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Status</p>
                <Badge
                  variant={
                    user.status === 'ACTIVE'
                      ? 'success'
                      : user.status === 'PENDING'
                      ? 'warning'
                      : 'danger'
                  }
                  className="mt-1"
                >
                  {user.status === 'ACTIVE'
                    ? 'Ativo'
                    : user.status === 'PENDING'
                    ? 'Pendente'
                    : user.status === 'BLOCKED'
                    ? 'Bloqueado'
                    : 'Inativo'}
                </Badge>
              </div>
            </div>

            {user.createdAt && (
              <div className="flex items-start gap-4 p-4 rounded-xl bg-slate-50/80 border border-slate-200/60 group hover:border-blue-200 hover:bg-blue-50/30 transition-all duration-200 sm:col-span-2">
                <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200/70 transition-colors">
                  <Clock className="text-blue-600" size={20} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Membro desde</p>
                  <p className="font-semibold text-foreground">{formatDate(user.createdAt)}</p>
                </div>
              </div>
            )}
          </div>
        </Card>

        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6 border-0 shadow-lg shadow-black/5 bg-gradient-to-br from-white to-slate-50/50">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Monitor className="text-blue-600" size={20} />
                </div>
                <h2 className="text-lg font-semibold">Sessões Ativas</h2>
              </div>
              {sessions.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={logoutAll}
                  disabled={isRefreshing}
                  className="text-destructive hover:text-destructive text-xs h-8 px-3"
                >
                  <LogOutIcon size={14} />
                  Sair de todas
                </Button>
              )}
            </div>

            {isLoadingSessions ? (
              <div className="space-y-3">
                <Skeleton variant="rectangular" height={72} className="rounded-xl" />
                <Skeleton variant="rectangular" height={72} className="rounded-xl" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Monitor size={32} className="text-slate-400" />
                </div>
                <p className="text-muted-foreground">Nenhuma sessão ativa encontrada</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sessions.map((session) => {
                  const DeviceIcon = getDeviceIcon(session.userAgent);
                  const isActive = session.id === activeSessionId;

                  return (
                    <div
                      key={session.id}
                      className={`relative flex items-center gap-3 p-4 rounded-xl border transition-all duration-200 ${
                        isActive
                          ? 'bg-gradient-to-r from-blue-50/80 to-blue-50/40 border-blue-200 shadow-sm shadow-blue-100'
                          : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
                      }`}
                    >
                      <div
                        className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                          isActive ? 'bg-blue-500 shadow-lg shadow-blue-200' : 'bg-slate-100'
                        }`}
                      >
                        <DeviceIcon
                          size={20}
                          className={isActive ? 'text-white' : 'text-slate-500'}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className={`font-medium text-sm truncate ${isActive ? 'text-blue-700' : 'text-foreground'}`}>
                            {session.userAgent?.split(')')[0]?.split('(')[1] || 'Dispositivo desconhecido'}
                          </p>
                          {isActive && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200 flex-shrink-0">
                              Atual
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {session.ipAddress && `${session.ipAddress} • `}
                          {formatRelative(session.createdAt)}
                        </p>
                      </div>
                      {!isActive && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleLogoutSession(session.id)}
                          className="text-slate-400 hover:text-red-500 flex-shrink-0 h-8 w-8"
                        >
                          <LogOut size={14} />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="pt-4">
              <Button
                variant="outline"
                className="w-full h-11 rounded-xl border-2 border-slate-200 hover:border-red-200 hover:bg-red-50/50 text-destructive hover:text-destructive transition-all duration-200"
                onClick={logout}
                disabled={isRefreshing}
              >
                <LogOut size={18} />
                Sair da conta
              </Button>
            </div>
          </Card>

          <div className="p-5 rounded-2xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/60 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Shield className="text-amber-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">Dica de Segurança</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Revise suas sessões ativas regularmente e saia de dispositivos que você não usa mais. Nunca compartilhe sua senha ou códigos de verificação.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
